import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";

interface Crumb {
  label: string;
  to?: string;
}

interface PageHeroProps {
  badge: string;
  badgeIcon?: string;
  title: ReactNode;
  subtitle?: string;
  crumbs: Crumb[];
  children?: ReactNode;
}

export default function PageHero({
  badge,
  badgeIcon = "ri-sparkling-2-line",
  title,
  subtitle,
  crumbs,
  children,
}: PageHeroProps) {
  return (
    <section className="relative overflow-hidden bg-foreground-950 text-background-50">
      <div className="absolute inset-0 nx-grid opacity-30"></div>
      <div className="nx-glow absolute -top-40 right-0 h-[460px] w-[460px] animate-pulseGlow"></div>
      <div className="nx-glow absolute bottom-0 -left-40 h-[360px] w-[360px] animate-pulseGlow"></div>

      <div className="relative mx-auto w-full max-w-7xl px-4 pt-28 pb-14 md:px-8 md:pt-36 md:pb-20">
        <Reveal>
          <nav
            aria-label="Trilha de navegação"
            className="flex flex-wrap items-center gap-2 text-xs text-background-400"
          >
            {crumbs.map((crumb, index) => (
              <span key={crumb.label} className="flex items-center gap-2">
                {crumb.to ? (
                  <Link to={crumb.to} className="transition-colors hover:text-background-50">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-background-200">{crumb.label}</span>
                )}
                {index < crumbs.length - 1 ? (
                  <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                ) : null}
              </span>
            ))}
          </nav>
        </Reveal>

        <Reveal delay={80}>
          <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-background-50/15 bg-background-50/5 px-4 py-1.5 text-xs font-medium tracking-wide text-accent-300">
            <i className={badgeIcon} aria-hidden="true"></i>
            {badge}
          </span>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <Reveal delay={140}>
              <h1 className="font-heading text-3xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-[3rem]">
                {title}
              </h1>
            </Reveal>
            {subtitle ? (
              <Reveal delay={200}>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-background-200 md:text-lg">
                  {subtitle}
                </p>
              </Reveal>
            ) : null}
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}